import { Checkbox, Input, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AutocompleteInput from 'src/components/shared/AutocompleteInput'
import { coerceParLevelInput } from 'src/utils/parLevelPaywall'
import {
  ORDERING_DEFAULTS_SECTION_HINT,
  SECONDARY_ZONE_COLUMN_TOOLTIP_DEFINITION,
} from 'src/utils/parLevelMarketingCopy'
import {
  ParLevelFieldTooltipContent,
  ZoneColumnTooltipContent,
} from 'src/utils/venueProductFieldTooltips'
import {
  EXCLUDE_FROM_GROSS_PROFIT_COLUMN_LABEL,
  EXCLUDE_FROM_GROSS_PROFIT_HELPER,
  EXCLUDE_FROM_GROSS_PROFIT_TOOLTIP,
  getExcludeFromGrossProfitDuplicateTitleHint,
  isVenueProductExcludedFromGrossProfit,
} from 'src/utils/excludeFromGrossProfit'
import wordCaps from 'src/utils/wordCaps'
import styled from 'styled-components/macro'
import { editVenue } from '../../features/VenueSlice'

const ParLevelAddonLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  .anticon {
    color: #1890ff;
    font-size: 12px;
    pointer-events: auto;
  }
`

const parLevelAddon = (
  <ParLevelAddonLabel>
    Par Level
    <Tooltip title={<ParLevelFieldTooltipContent />}>
      <InfoCircleOutlined />
    </Tooltip>
  </ParLevelAddonLabel>
)

const EditVenueProductModal = ({ product, setEditedProduct }) => {
  const venueProducts = useSelector(state => state.venueProducts.products)
  const account = useSelector(state => state.auth.account)
  const venue = useSelector(state => state.venue.venue)
  const dispatch = useDispatch()

  const isAdmin = account && account.isAdmin
  const isTFRAdmin = account && account.TFRAdmin
  const isJustAdmin = account && account.JUSTAdmin
  const isGreenAdmin = account && account.GREENAdmin
  const secondaryZones = venue?.secondaryZones || []

  const existingZones = [
    ...new Set(venueProducts.map(product => wordCaps(product.zone).trim())),
  ]
  const existingTypes = [
    ...new Set(venueProducts.map(product => wordCaps(product.type).trim())),
  ]
  const duplicateTitleHint = getExcludeFromGrossProfitDuplicateTitleHint({
    product,
    products: venueProducts,
  })

  return (
    <Wrapper>
      <SectionTitle>Product details</SectionTitle>

      <Row>
        <InputComp
          value={product.title}
          onChange={e => {
            setEditedProduct({ ...product, title: e.target.value })
          }}
          addonBefore='Title'
          placeholder='Title'
        />
      </Row>

      <TwoColumnRow>
        <Column>
          <AutocompleteInput
            fieldName='Zone'
            tooltip={<ZoneColumnTooltipContent />}
            options={existingZones}
            selected={product.zone}
            onChange={val => {
              setEditedProduct({
                ...product,
                zone: val,
              })
            }}
          />
        </Column>
        <Column>
          <AutocompleteInput
            fieldName='Secondary Zone'
            tooltip={SECONDARY_ZONE_COLUMN_TOOLTIP_DEFINITION}
            options={secondaryZones}
            selected={product.secondaryZone}
            onChange={val => {
              if (val && !secondaryZones.includes(val)) {
                const updatedVenue = {
                  ...venue,
                  secondaryZones: [...secondaryZones, val],
                }
                dispatch(editVenue(updatedVenue, venue._id))
              }

              setEditedProduct({
                ...product,
                secondaryZone: val,
              })
            }}
          />
        </Column>
      </TwoColumnRow>

      <TwoColumnRow>
        <Column>
          <InputComp
            value={product.nominalCode || ''}
            onChange={e => {
              setEditedProduct({
                ...product,
                nominalCode: e.target.value,
              })
            }}
            addonBefore='Nominal Code'
            placeholder='Nominal Code'
          />
        </Column>
        <Column>
          <AutocompleteInput
            fieldName='Type'
            options={existingTypes}
            selected={product.type}
            onChange={val => {
              setEditedProduct({
                ...product,
                type: val,
              })
            }}
          />
        </Column>
      </TwoColumnRow>

      <SectionTitle>Ordering defaults</SectionTitle>
      <SectionHint>{ORDERING_DEFAULTS_SECTION_HINT}</SectionHint>

      <TwoColumnRow>
        <Column>
          <InputComp
            value={product.defaultQuantity}
            onChange={e => {
              setEditedProduct({
                ...product,
                defaultQuantity: e.target.value,
              })
            }}
            addonBefore='Default Quantity'
            placeholder='Default Quantity'
          />
        </Column>
        <Column>
          <InputComp
            data-testid='par-level-input'
            value={
              product.parLevel == null || product.parLevel === ''
                ? '0'
                : String(product.parLevel)
            }
            onChange={e => {
              setEditedProduct({
                ...product,
                parLevel: coerceParLevelInput(e.target.value),
              })
            }}
            addonBefore={parLevelAddon}
            placeholder='0'
            inputMode='decimal'
          />
        </Column>
      </TwoColumnRow>

      <SectionTitle>Limits &amp; flags</SectionTitle>

      <Row>
        <InputComp
          value={product.monthlyLimit}
          onChange={e => {
            setEditedProduct({
              ...product,
              monthlyLimit: e.target.value,
            })
          }}
          addonBefore='Monthly Order Limit'
          placeholder='Leave blank for no limit'
        />
      </Row>

      <CheckboxRow>
        <Checkbox
          onChange={() => {
            setEditedProduct({
              ...product,
              isFavourite: !product.isFavourite,
            })
          }}
          checked={product.isFavourite}
        >
          Is Favourite
        </Checkbox>
      </CheckboxRow>

      <FlagBlock>
        <CheckboxRow>
          <Checkbox
            data-testid='exclude-from-gp-checkbox'
            onChange={() => {
              setEditedProduct({
                ...product,
                excludeFromGrossProfit:
                  !isVenueProductExcludedFromGrossProfit(product),
              })
            }}
            checked={isVenueProductExcludedFromGrossProfit(product)}
          >
            {EXCLUDE_FROM_GROSS_PROFIT_COLUMN_LABEL}
          </Checkbox>
          <Tooltip title={EXCLUDE_FROM_GROSS_PROFIT_TOOLTIP}>
            <InfoCircleOutlined
              style={{ marginLeft: 6, color: '#8c8c8c', fontSize: 12 }}
            />
          </Tooltip>
        </CheckboxRow>
        <FlagHint>{EXCLUDE_FROM_GROSS_PROFIT_HELPER}</FlagHint>
        {duplicateTitleHint ? (
          <FlagHint data-testid='exclude-from-gp-duplicate-title-hint'>
            {duplicateTitleHint}
          </FlagHint>
        ) : null}
      </FlagBlock>

      {isAdmin || isTFRAdmin || isJustAdmin || isGreenAdmin ? (
        <CheckboxRow>
          <Checkbox
            onChange={() => {
              setEditedProduct({
                ...product,
                isSpecialProduct: !product.isSpecialProduct,
              })
            }}
            checked={product.isSpecialProduct}
          >
            Is Special
          </Checkbox>
        </CheckboxRow>
      ) : null}

      {isAdmin || isTFRAdmin || isJustAdmin || isGreenAdmin
        ? product.isSpecialProduct && (
            <SpecialPricingSection>
              <Row>
                <InputComp
                  value={isNaN(product.specialPrice) ? 0 : product.specialPrice}
                  onChange={e => {
                    if (e.target.value > 0 || e.target.value === '') {
                      setEditedProduct({
                        ...product,
                        specialPrice: e.target.value,
                      })
                    }
                  }}
                  addonBefore='Special price'
                  placeholder='Special price'
                />
              </Row>
              <Row>
                <InputComp
                  type='number'
                  value={
                    isNaN(product.OMSSpecialPrice) ? 0 : product.OMSSpecialPrice
                  }
                  onChange={e => {
                    if (e.target.value > 0 || e.target.value === '') {
                      setEditedProduct({
                        ...product,
                        OMSSpecialPrice: e.target.value,
                      })
                    }
                  }}
                  addonBefore='Oms Special price'
                  placeholder='Oms Special price'
                />
              </Row>
            </SpecialPricingSection>
          )
        : null}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding-top: 4px;
`

const InputComp = styled(Input)``

const SectionTitle = styled.h4`
  margin: 20px 0 12px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colours?.oliveGrey || '#666'};

  &:first-child {
    margin-top: 0;
  }
`

const SectionHint = styled.p`
  margin: -4px 0 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colours?.oliveGrey || '#666'};
  line-height: 1.45;
`

const Row = styled.div`
  margin-bottom: 12px;
`

const TwoColumnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`

const Column = styled.div`
  flex: 1 1 240px;
  min-width: 0;
`

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  margin: 8px 0 12px;
`

const FlagBlock = styled.div`
  margin: 8px 0 12px;

  ${CheckboxRow} {
    margin: 0;
  }
`

const FlagHint = styled.p`
  margin: 4px 0 0 24px;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours?.oliveGrey || '#666'};
`

const SpecialPricingSection = styled.div`
  margin-top: 4px;
  padding: 12px 12px 4px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colours?.mayoWhite || '#fafafa'};
  border: 1px solid ${({ theme }) => theme.colours?.panSilver || '#e8e8e8'};
`

export default EditVenueProductModal
